/**
 * HeritageTab - Pestaña de patrimonio, fenología y datos administrativos
 *
 * @version 1.0
 * @updated 2026-02-12
 * @module core/map-tools/TreeDetailsPanel
 */

import type { Tree } from '../../tree-types';
import { FormLabel } from '../FormLabel';

export interface HeritageTabProps {
  tree: Tree;
  onChange: (field: keyof Tree, value: any) => void;
}

export function HeritageTab({ tree, onChange }: HeritageTabProps): JSX.Element {
  // Handler para servicios ecosistémicos (multi-select)
  const handleEcosystemServicesChange = (value: string, checked: boolean) => {
    const currentServices = tree.ecosystemServices || [];
    const newServices = checked
      ? [...currentServices, value]
      : currentServices.filter(s => s !== value);
    onChange('ecosystemServices', newServices);
  };

  return (
    <div className="tree-tab-panel">
      {/* Sección: Valor Patrimonial y Ecológico */}
      <div className="tree-form-section">
        <h3 className="tree-form-section-title">Valor Patrimonial y Ecológico</h3>

        <div className="tree-form-row">
          <div className="tree-form-group">
            <FormLabel tooltip="Valor estético y paisajístico del árbol. Considera floración, color de follaje, forma, porte y contribución visual al espacio urbano. Árboles excepcionales son hitos paisajísticos.">
              Valor ornamental
            </FormLabel>
            <select
              className="tree-form-select"
              value={tree.ornamentalValue}
              onChange={(e) => onChange('ornamentalValue', e.target.value)}
            >
              <option value="">Seleccionar...</option>
              <option value="low">Bajo</option>
              <option value="medium">Medio</option>
              <option value="high">Alto</option>
              <option value="exceptional">Excepcional</option>
            </select>
          </div>

          <div className="tree-form-group">
            <FormLabel tooltip="Importancia del árbol para el ecosistema urbano. Considera producción de oxígeno, captura de CO₂, hábitat para fauna, mejora del microclima y otros servicios ambientales.">
              Valor ecológico
            </FormLabel>
            <select
              className="tree-form-select"
              value={tree.ecologicalValue}
              onChange={(e) => onChange('ecologicalValue', e.target.value)}
            >
              <option value="">Seleccionar...</option>
              <option value="low">Bajo</option>
              <option value="medium">Medio</option>
              <option value="high">Alto</option>
            </select>
          </div>
        </div>

        <div className="tree-form-checkbox-group">
          <input
            type="checkbox"
            id="hasHistoricalValue"
            className="tree-form-checkbox"
            checked={tree.hasHistoricalValue}
            onChange={(e) => onChange('hasHistoricalValue', e.target.checked)}
          />
          <label htmlFor="hasHistoricalValue" className="tree-form-checkbox-label">
            Tiene valor histórico/patrimonial
          </label>
        </div>

        {tree.hasHistoricalValue && (
          <div className="tree-form-group">
            <FormLabel tooltip="Relato de la importancia histórica del árbol: eventos asociados, personas relevantes, hitos urbanos, testimonios de antigüedad, etc. Fundamental para su preservación.">
              Descripción histórica
            </FormLabel>
            <textarea
              className="tree-form-textarea"
              value={tree.historicalDescription}
              onChange={(e) => onChange('historicalDescription', e.target.value)}
              placeholder="Describir importancia histórica, eventos relevantes, etc."
            />
          </div>
        )}

        <div className="tree-form-checkbox-group">
          <input
            type="checkbox"
            id="isProtected"
            className="tree-form-checkbox"
            checked={tree.isProtected}
            onChange={(e) => onChange('isProtected', e.target.checked)}
          />
          <label htmlFor="isProtected" className="tree-form-checkbox-label">
            Árbol protegido/catalogado
          </label>
        </div>

        {tree.isProtected && (
          <div className="tree-form-group">
            <FormLabel tooltip="Número y año del instrumento legal que declara al árbol como protegido o de interés patrimonial. Impide su extracción sin autorización especial.">
              Decreto/Ley de protección
            </FormLabel>
            <input
              type="text"
              className="tree-form-input"
              value={tree.protectionLaw}
              onChange={(e) => onChange('protectionLaw', e.target.value)}
              placeholder="Ej: Decreto 123/2020, Ley Provincial 4567"
            />
          </div>
        )}

        <div className="tree-form-group">
          <FormLabel tooltip="Indica si la especie está en alguna categoría de conservación (vulnerable, en peligro, crítica) según listas rojas nacionales o internacionales (IUCN).">
            Especie amenazada
          </FormLabel>
          <div className="tree-form-checkbox-group">
            <input
              type="checkbox"
              id="isEndangeredSpecies"
              className="tree-form-checkbox"
              checked={tree.isEndangeredSpecies}
              onChange={(e) => onChange('isEndangeredSpecies', e.target.checked)}
            />
            <label htmlFor="isEndangeredSpecies" className="tree-form-checkbox-label">
              Especie amenazada/en peligro
            </label>
          </div>
        </div>

        <div className="tree-form-group">
          <FormLabel tooltip="Beneficios que el árbol proporciona al ambiente urbano y la sociedad. Los servicios ecosistémicos justifican económicamente la inversión en arbolado público.">
            Servicios ecosistémicos proporcionados
          </FormLabel>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '8px' }}>
            {[
              { value: 'co2', label: 'Captura de CO₂' },
              { value: 'sombra', label: 'Provisión de sombra' },
              { value: 'habitat', label: 'Hábitat para fauna' },
              { value: 'regulacion-termica', label: 'Regulación térmica' },
              { value: 'drenaje', label: 'Mejora del drenaje' },
              { value: 'calidad-aire', label: 'Mejora de calidad del aire' },
              { value: 'paisaje', label: 'Valor paisajístico' },
            ].map(({ value, label }) => (
              <div key={value} className="tree-form-checkbox-group">
                <input
                  type="checkbox"
                  id={`ecosystem-${value}`}
                  className="tree-form-checkbox"
                  checked={(tree.ecosystemServices ?? []).includes(value)}
                  onChange={(e) => handleEcosystemServicesChange(value, e.target.checked)}
                />
                <label htmlFor={`ecosystem-${value}`} className="tree-form-checkbox-label">
                  {label}
                </label>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Sección: Fenología */}
      <div className="tree-form-section">
        <h3 className="tree-form-section-title">Fenología</h3>

        <div className="tree-form-group">
          <FormLabel tooltip="Perenne: mantiene follaje todo el año. Caducifolio: pierde hojas en otoño/invierno. Semi-caducifolio: pierde parte del follaje o según clima.">
            Tipo de follaje
          </FormLabel>
          <select
            className="tree-form-select"
            value={tree.foliageType}
            onChange={(e) => onChange('foliageType', e.target.value)}
          >
            <option value="">Seleccionar...</option>
            <option value="evergreen">Perenne</option>
            <option value="deciduous">Caducifolio</option>
            <option value="semi-deciduous">Semi-caducifolio</option>
          </select>
        </div>

        <div className="tree-form-row">
          <div className="tree-form-group">
            <FormLabel tooltip="Período del año en que el árbol produce flores. Importante para planificación de polinizadores, alergias y valor ornamental estacional.">
              Época de floración
            </FormLabel>
            <input
              type="text"
              className="tree-form-input"
              value={tree.floweringSeason}
              onChange={(e) => onChange('floweringSeason', e.target.value)}
              placeholder="Ej: Primavera, Sept-Nov"
            />
          </div>

          <div className="tree-form-group">
            <FormLabel tooltip="Período de producción de frutos o semillas. Relevante para fauna silvestre, limpieza de espacios públicos y potencial propagación.">
              Época de fructificación
            </FormLabel>
            <input
              type="text"
              className="tree-form-input"
              value={tree.fruitingSeason}
              onChange={(e) => onChange('fruitingSeason', e.target.value)}
              placeholder="Ej: Verano, Ene-Mar"
            />
          </div>
        </div>

        <div className="tree-form-row">
          <div className="tree-form-group">
            <FormLabel tooltip="Tasa de crecimiento anual de la especie. Lenta: <30cm/año. Media: 30-60cm/año. Rápida: >60cm/año. Afecta mantenimiento y selección de especies.">
              Velocidad de crecimiento
            </FormLabel>
            <select
              className="tree-form-select"
              value={tree.growthRate}
              onChange={(e) => onChange('growthRate', e.target.value)}
            >
              <option value="">Seleccionar...</option>
              <option value="slow">Lenta</option>
              <option value="medium">Media</option>
              <option value="fast">Rápida</option>
            </select>
          </div>

          <div className="tree-form-group">
            <FormLabel tooltip="Necesidad de agua de la especie. Bajo: toleran sequía. Medio: riego ocasional. Alto: requieren riego frecuente. Fundamental para gestión hídrica urbana.">
              Requerimientos hídricos
            </FormLabel>
            <select
              className="tree-form-select"
              value={tree.waterRequirements}
              onChange={(e) => onChange('waterRequirements', e.target.value)}
            >
              <option value="">Seleccionar...</option>
              <option value="low">Bajos</option>
              <option value="medium">Medios</option>
              <option value="high">Altos</option>
            </select>
          </div>
        </div>
      </div>

      {/* Sección: Datos Administrativos */}
      <div className="tree-form-section">
        <h3 className="tree-form-section-title">Datos Administrativos</h3>

        <div className="tree-form-row">
          <div className="tree-form-group">
            <FormLabel tooltip="Entidad responsable del árbol. Define jurisdicción para autorizaciones de poda/extracción y obligaciones de mantenimiento según normativa local.">
              Propietario/Responsable
            </FormLabel>
            <select
              className="tree-form-select"
              value={tree.owner}
              onChange={(e) => onChange('owner', e.target.value)}
            >
              <option value="">Seleccionar...</option>
              <option value="municipal">Municipal</option>
              <option value="provincial">Provincial</option>
              <option value="private">Privado</option>
              <option value="other">Otro</option>
            </select>
          </div>

          <div className="tree-form-group">
            <FormLabel tooltip="Estado actual del árbol en el inventario. Activo: en pie y saludable. Inactivo: requiere revisión. Programado para extracción: autorizado para remoción. Extraído: ya removido del sitio.">
              Estado del registro
            </FormLabel>
            <select
              className="tree-form-select"
              value={tree.registryStatus}
              onChange={(e) => onChange('registryStatus', e.target.value)}
            >
              <option value="active">Activo</option>
              <option value="inactive">Inactivo</option>
              <option value="scheduled-removal">Programado para extracción</option>
              <option value="removed">Extraído</option>
            </select>
          </div>
        </div>

        <div className="tree-form-group">
          <FormLabel tooltip="Secretaría, departamento o división municipal responsable de la gestión y mantenimiento del arbolado en esta área geográfica.">
            Área de gestión
          </FormLabel>
          <input
            type="text"
            className="tree-form-input"
            value={tree.managementArea}
            onChange={(e) => onChange('managementArea', e.target.value)}
            placeholder="Ej: Secretaría de Medio Ambiente, Departamento Parques y Jardines"
          />
        </div>

        <div className="tree-form-group">
          <FormLabel tooltip="Campo de texto libre para cualquier información adicional relevante que no encaje en los campos específicos. Incluir observaciones especiales, particularidades, contexto local, etc.">
            Observaciones generales
          </FormLabel>
          <textarea
            className="tree-form-textarea"
            value={tree.generalNotes}
            onChange={(e) => onChange('generalNotes', e.target.value)}
            placeholder="Notas adicionales, observaciones relevantes, etc."
            rows={6}
          />
        </div>

        {/* Información de sistema (read-only) */}
        <div className="tree-form-row">
          <div className="tree-form-group">
            <FormLabel tooltip="Fecha y hora exacta en que el árbol fue registrado por primera vez en el sistema. Campo automático no editable.">
              Fecha de registro
            </FormLabel>
            <input
              type="text"
              className="tree-form-input"
              value={tree.registrationDate?.toLocaleString() ?? ''}
              disabled
              readOnly
            />
          </div>

          <div className="tree-form-group">
            <FormLabel tooltip="Usuario o técnico que realizó el registro inicial del árbol en el sistema. Campo automático no editable.">
              Registrado por
            </FormLabel>
            <input
              type="text"
              className="tree-form-input"
              value={tree.registeredBy}
              disabled
              readOnly
            />
          </div>
        </div>

        <div className="tree-form-row">
          <div className="tree-form-group">
            <FormLabel tooltip="Fecha y hora de la última modificación de cualquier campo del registro. Se actualiza automáticamente al guardar cambios.">
              Última actualización
            </FormLabel>
            <input
              type="text"
              className="tree-form-input"
              value={tree.lastUpdate?.toLocaleString() ?? ''}
              disabled
              readOnly
            />
          </div>

          <div className="tree-form-group">
            <FormLabel tooltip="Usuario que realizó la última modificación del registro. Permite auditoría y seguimiento de cambios.">
              Actualizado por
            </FormLabel>
            <input
              type="text"
              className="tree-form-input"
              value={tree.updatedBy}
              disabled
              readOnly
            />
          </div>
        </div>
      </div>
    </div>
  );
}
